import { Stack, Typography } from '@mui/material';
import {
  DvsStatus as DvsStatusComponent,
  IdentityDocumentType,
  TDocumentVerificationStatusActions,
  useConfirm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useDvsData } from './hooks/useDvsData';

/**
 * Not Applicable gets its own wording because it is not an opinion on the document. It states a
 * lack of capability: DVS has no source to check this document against, so no check was or ever
 * will be run. A document issued overseas is the typical case rather than the only one. An
 * administrator choosing it needs to see that distinction before confirming, rather than the
 * generic "override the DVS status" prompt.
 */
function confirmationContentFor(status: keyof TDocumentVerificationStatusActions) {
  if (status === 'NotApplicable') {
    return (
      <Stack gap={tokens.spacingBase}>
        <Typography>
          The action you are about to carry out will record that DVS cannot check this document
          against a source, a foreign document that has to be verified manually being the typical
          case. It states that no source check was, or ever will be, performed. Do you wish to
          proceed?
        </Typography>
        <Typography variant="h6">
          NB: The document keeps only the confidence earned by its capture.
        </Typography>
      </Stack>
    );
  }

  return (
    <Typography>
      The action you are about to carry out will manually override the DVS status. Do you wish to
      proceed?
    </Typography>
  );
}

export function DvsStatus({
  customerId,
  documentType,
}: Readonly<{
  customerId: string;
  documentType: keyof typeof IdentityDocumentType;
}>) {
  const { status, dvsErrorDetails, resetStatus, resubmit } = useDvsData(customerId, documentType);
  const confirm = useConfirm();

  /**
   * Runs `action` once the administrator accepts the dialogue. Two rejections end here, each
   * deliberately silent:
   * - Cancelling the dialogue rejects `confirm` with no reason. Nothing was asked of person-api, so
   *   there is nothing to report.
   * - A refused request: `useDvsData` has already shown the error toast before it rethrows, so a
   *   second report here would double it. The panel stays as it was and can be used again.
   */
  const runWhenConfirmed = async (
    options: Parameters<typeof confirm>[0],
    action: () => Promise<unknown>,
  ) => {
    // Fail closed. With no ConfirmProvider mounted, `confirm` is the context default, which returns
    // undefined instead of a promise. Awaiting that would run the action unconfirmed, and these
    // actions cost money or override a status. The dialogue resolves with undefined on confirm, so
    // the check is on what `confirm` returns, never on what it resolves to.
    const asked: unknown = confirm(options);
    if (typeof (asked as PromiseLike<unknown> | undefined)?.then !== 'function') {
      return;
    }

    try {
      await asked;
    } catch {
      return;
    }

    try {
      await action();
    } catch {
      // Already reported by `useDvsData`; see above.
    }
  };

  const handleResetStatus = (status: keyof TDocumentVerificationStatusActions) =>
    runWhenConfirmed(
      {
        content: confirmationContentFor(status),
        confirmationText: 'Confirm',
      },
      () => resetStatus(status),
    );

  const handleResubmit = () =>
    runWhenConfirmed(
      {
        content: (
          <Stack gap={tokens.spacingBase}>
            <Typography>
              The action you are about to carry out will resubmit the identity document for DVS
              validation? Please confirm you wish to proceed?
            </Typography>
            <Typography variant="h6">NB: This action incurs external costs from DVS.</Typography>
          </Stack>
        ),
        confirmationText: 'Confirm',
      },
      resubmit,
    );

  // Only pass onResubmit if document type allows resubmission. DVS has no source to check an
  // other identity document against, so its Re Submit stays on screen, disabled.
  const canResubmit = documentType !== IdentityDocumentType.OtherIdentityDocument;
  // A photo card gets Not Applicable on capture, so there is nothing to resubmit and Re Submit is
  // left out altogether (verifime-adr#815). An ImmiCard is sent to DVS like a passport, so it keeps
  // Re Submit. Set Status stays for both, so an administrator can still record a status.
  const hideResubmit = documentType === IdentityDocumentType.PhotoCard;

  return (
    <DvsStatusComponent
      status={status}
      errorDetails={dvsErrorDetails}
      onResetStatus={handleResetStatus}
      onResubmit={canResubmit ? handleResubmit : undefined}
      hideResubmit={hideResubmit}
    />
  );
}
